import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CONTACT } from "../../lib/contact";
import { useT } from "../../i18n/LanguageContext";

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { t, lang, setLang } = useT();
    const { pathname } = useLocation();
    const onHome = pathname === "/";

    // On Home, keep pure hash links so the browser handles smooth scroll inside the page.
    // On any other route (e.g. /case-studies/:slug), send users back to Home with a hash
    // so ScrollToTop can jump to the right section. The "work" entry is intercepted
    // upstream in both nav maps (desktop + mobile) and always routes to /case-studies
    // — it never reaches this helper.
    const hrefForLink = (link) => {
        if (onHome) return link.hash;
        return `/${link.hash}`;
    };

    const links = [
        { hash: "#manifesto", label: t("nav.manifesto"), key: "manifesto" },
        { hash: "#services", label: t("nav.services"), key: "services" },
        { hash: "#process", label: t("nav.process"), key: "process" },
        { hash: "#work", label: t("nav.work"), key: "work" },
        { hash: "#pricing", label: t("nav.pricing"), key: "pricing" },
        { hash: "/blog", label: t("nav.blog") || "Blog", key: "blog" },
        { hash: "#contact", label: t("nav.contact"), key: "contact" },
    ];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const toggle = (
        <div
            role="group"
            aria-label={t("toggle.aria")}
            data-testid="lang-toggle"
            className="inline-flex items-center border border-warm rounded-none text-xs"
        >
            <button
                type="button"
                onClick={() => setLang("en")}
                data-testid="lang-toggle-en"
                aria-pressed={lang === "en"}
                className={`px-2.5 py-1 tracking-[0.18em] uppercase transition-colors ${
                    lang === "en"
                        ? "bg-ink text-sand"
                        : "text-ink/70 hover:text-ink"
                }`}
            >
                {t("toggle.en")}
            </button>
            <button
                type="button"
                onClick={() => setLang("es")}
                data-testid="lang-toggle-es"
                aria-pressed={lang === "es"}
                className={`px-2.5 py-1 tracking-[0.18em] uppercase transition-colors ${
                    lang === "es"
                        ? "bg-ink text-sand"
                        : "text-ink/70 hover:text-ink"
                }`}
            >
                {t("toggle.es")}
            </button>
        </div>
    );

    return (
        <header
            data-testid="site-nav"
            data-cta-source="nav"
            className={`relative z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-[#F9F6F0]/85 backdrop-blur-md border-b border-warm"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
                <a
                    href={onHome ? "#top" : "/"}
                    data-testid="nav-logo"
                    className="font-serif text-2xl md:text-3xl tracking-tight text-ink"
                >
                    gfirem<span className="text-terracotta">.</span>
                </a>
                <nav className="hidden lg:flex items-center gap-10">
                    {links.map((l) =>
                        // "work" always routes to /case-studies index; "blog" routes to /blog.
                        // All other entries keep smooth-scroll behavior on home and Link+hash
                        // on other routes.
                        l.key === "work" ? (
                            <Link
                                key={l.key}
                                to="/case-studies"
                                data-testid={`nav-${l.key}-link`}
                                className="text-sm text-ink/80 hover:text-ink link-underline transition-colors"
                            >
                                {l.label}
                            </Link>
                        ) : l.key === "blog" ? (
                            <Link
                                key={l.key}
                                to="/blog"
                                data-testid={`nav-${l.key}-link`}
                                className="text-sm text-ink/80 hover:text-ink link-underline transition-colors"
                            >
                                {l.label}
                            </Link>
                        ) : onHome ? (
                            <a
                                key={l.key}
                                href={l.hash}
                                data-testid={`nav-${l.key}-link`}
                                className="text-sm text-ink/80 hover:text-ink link-underline transition-colors"
                            >
                                {l.label}
                            </a>
                        ) : (
                            <Link
                                key={l.key}
                                to={hrefForLink(l)}
                                data-testid={`nav-${l.key}-link`}
                                className="text-sm text-ink/80 hover:text-ink link-underline transition-colors"
                            >
                                {l.label}
                            </Link>
                        )
                    )}
                </nav>
                <div className="hidden lg:flex items-center gap-5">
                    {toggle}
                    <a
                        href={CONTACT.calendlyHref}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="nav-book-call-button"
                        className="inline-flex items-center gap-2 bg-ink text-sand px-5 py-2.5 text-sm whitespace-nowrap hover:bg-[var(--ink-soft)] transition-colors"
                    >
                        {t("nav.bookCall")}
                        <span aria-hidden>→</span>
                    </a>
                </div>
                <div className="lg:hidden flex items-center gap-3">
                    {toggle}
                    <button
                        aria-label="Menu"
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="w-10 h-10 inline-flex items-center justify-center text-ink"
                    >
                        <span className="block w-6 h-px bg-ink relative before:content-[''] before:absolute before:left-0 before:-top-2 before:w-6 before:h-px before:bg-ink after:content-[''] after:absolute after:left-0 after:top-2 after:w-6 after:h-px after:bg-ink" />
                    </button>
                </div>
            </div>
            {open && (
                <div
                    data-testid="nav-mobile-menu"
                    className="lg:hidden border-t border-warm bg-[#F9F6F0]/95 backdrop-blur-md"
                >
                    <div className="px-6 py-6 flex flex-col gap-4">
                        {links.map((l) =>
                            l.key === "work" ? (
                                <Link
                                    key={l.key}
                                    to="/case-studies"
                                    data-testid={`nav-mobile-${l.key}-link`}
                                    onClick={() => setOpen(false)}
                                    className="text-lg font-serif"
                                >
                                    {l.label}
                                </Link>
                            ) : l.key === "blog" ? (
                                <Link
                                    key={l.key}
                                    to="/blog"
                                    data-testid={`nav-mobile-${l.key}-link`}
                                    onClick={() => setOpen(false)}
                                    className="text-lg font-serif"
                                >
                                    {l.label}
                                </Link>
                            ) : onHome ? (
                                <a
                                    key={l.key}
                                    href={l.hash}
                                    data-testid={`nav-mobile-${l.key}-link`}
                                    onClick={() => setOpen(false)}
                                    className="text-lg font-serif"
                                >
                                    {l.label}
                                </a>
                            ) : (
                                <Link
                                    key={l.key}
                                    to={hrefForLink(l)}
                                    data-testid={`nav-mobile-${l.key}-link`}
                                    onClick={() => setOpen(false)}
                                    className="text-lg font-serif"
                                >
                                    {l.label}
                                </Link>
                            )
                        )}
                        <a
                            href={CONTACT.calendlyHref}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-2 inline-flex items-center justify-center bg-ink text-sand px-5 py-3 text-sm"
                        >
                            {t("nav.bookCall")} →
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
